import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, startOfDay, endOfDay, subDays, isToday } from "date-fns";
import toast from "react-hot-toast";
import { Award, Ban, CalendarCheck, DoorClosed, MapPin, Phone, Route as RouteIcon, Users } from "lucide-react";
import { fetchDashboardKpis, type DashboardKpis } from "@/services/dashboard";
import {
  appointmentAddress,
  appointmentLeadName,
  listOpenAppointments,
  updateAppointmentStatus,
  type AppointmentRow,
} from "@/services/appointments";
import { listRoutes } from "@/services/routes";
import { useAuthStore } from "@/store/auth";
import { useIsAdmin } from "@/hooks/useCurrentUser";
import { formatPhoneDisplay, toTelHref } from "@/lib/contactLinks";

/**
 * Dashboard — six KPI cards covering route lifecycle + lead status events.
 *
 * Scope toggle (admin only): "Mine" filters to the current user's actions;
 * "Team" shows team-wide aggregates. Non-admins see only their own — that's
 * enforced server-side in dashboard_kpis() so the toggle stays disabled here.
 *
 * Date range presets: Today / 7 days / 30 days / All time / Custom. Status
 * counts are bounded by lead_status_history.changed_at; route counts are
 * current-state snapshots (in_progress + pool) so the range doesn't affect
 * them — that's documented inline on the cards.
 */

type RangePreset = "today" | "7d" | "30d" | "all" | "custom";

interface Range {
  from: Date | null;
  to: Date | null;
  preset: RangePreset;
}

function buildRange(preset: RangePreset, custom?: { from: Date; to: Date }): Range {
  const now = new Date();
  if (preset === "today") return { from: startOfDay(now), to: endOfDay(now), preset };
  if (preset === "7d") return { from: startOfDay(subDays(now, 6)), to: endOfDay(now), preset };
  if (preset === "30d") return { from: startOfDay(subDays(now, 29)), to: endOfDay(now), preset };
  if (preset === "custom" && custom) {
    return { from: startOfDay(custom.from), to: endOfDay(custom.to), preset };
  }
  return { from: null, to: null, preset: "all" };
}

interface DashboardPrefs {
  preset: RangePreset;
  customFrom: string;
  customTo: string;
  scope: "mine" | "team";
}

function dashboardPrefsKey(teamId: string | undefined): string {
  // Per-team key so admins switching tenants don't drag preferences across
  // orgs (matches the leads/routes filter persistence pattern).
  return `myroute:dashboard:prefs:v1:${teamId ?? "anon"}`;
}

function readDashboardPrefs(teamId: string | undefined): DashboardPrefs {
  const today = format(new Date(), "yyyy-MM-dd");
  const defaults: DashboardPrefs = { preset: "today", customFrom: today, customTo: today, scope: "mine" };
  if (typeof window === "undefined") return defaults;
  try {
    const raw = window.localStorage.getItem(dashboardPrefsKey(teamId));
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<DashboardPrefs>;
    return {
      preset: (["today", "7d", "30d", "all", "custom"] as const).includes(parsed.preset as never)
        ? (parsed.preset as RangePreset)
        : "today",
      customFrom: typeof parsed.customFrom === "string" ? parsed.customFrom : today,
      customTo: typeof parsed.customTo === "string" ? parsed.customTo : today,
      scope: parsed.scope === "team" ? "team" : "mine",
    };
  } catch {
    return defaults;
  }
}

export function DashboardPage() {
  const me = useAuthStore((s) => s.user);
  // Elevated = admin_user OR route_manager. Both can see the Mine/Team
  // toggle since route_managers also drive team-wide visibility decisions.
  const isAdmin = useIsAdmin();

  // Per-team preference persistence so managers who live in "30-day Team
  // view" don't have to re-toggle every visit. Key is namespaced to
  // user.teamId so switching tenants doesn't drag preferences across orgs.
  const prefsKey = useMemo(() => dashboardPrefsKey(me?.teamId), [me?.teamId]);
  const initialPrefs = useMemo(() => readDashboardPrefs(me?.teamId), [me?.teamId]);

  const [range, setRange] = useState<Range>(() =>
    initialPrefs.preset === "custom"
      ? buildRange("custom", { from: parseISO(initialPrefs.customFrom), to: parseISO(initialPrefs.customTo) })
      : buildRange(initialPrefs.preset),
  );
  const [customFrom, setCustomFrom] = useState<string>(initialPrefs.customFrom);
  const [customTo, setCustomTo] = useState<string>(initialPrefs.customTo);
  // Mine vs Team. For non-admins this is fixed to "mine" server-side; the
  // toggle is hidden in that case.
  const [scope, setScope] = useState<"mine" | "team">(initialPrefs.scope);

  // Persist preferences on every change. Lightweight payload — single
  // localStorage write per state mutation, no debounce needed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: DashboardPrefs = {
      preset: range.preset,
      customFrom,
      customTo,
      scope,
    };
    window.localStorage.setItem(prefsKey, JSON.stringify(payload));
  }, [prefsKey, range.preset, customFrom, customTo, scope]);

  const userParam = useMemo(() => {
    if (!isAdmin) return undefined; // non-admins: server forces self
    return scope === "mine" ? me?.id : null;
  }, [scope, isAdmin, me?.id]);

  const kpisQuery = useQuery<DashboardKpis>({
    queryKey: ["dashboard", "kpis", { from: range.from?.toISOString() ?? null, to: range.to?.toISOString() ?? null, user: userParam }],
    queryFn: () => fetchDashboardKpis({
      from: range.from?.toISOString() ?? null,
      to: range.to?.toISOString() ?? null,
      user: userParam,
    }),
    staleTime: 30_000,
  });

  // "Your active route" card is unchanged from the old dashboard — keeps the
  // driver landing experience intact.
  const routesQuery = useQuery({ queryKey: ["routes", "list"], queryFn: () => listRoutes() });
  const routes = routesQuery.data ?? [];
  const myActiveRoute = routes.find(
    (r) => r.assigned_to === me?.id && ["draft", "published", "in_progress"].includes(r.status),
  );

  const k = kpisQuery.data;

  // Click handlers — drill from a card into the relevant filtered list.
  const navigate = useNavigate();
  function openLeadsList(status: string) {
    // Carry the scope context: when an admin is viewing "mine", pass
    // changedBy=<self> so the list reflects what they personally did in
    // the window. Otherwise leave changedBy unset (team-wide).
    const params = new URLSearchParams({ status });
    if (range.from) params.set("from", range.from.toISOString());
    if (range.to) params.set("to", range.to.toISOString());
    if (userParam) params.set("changedBy", userParam);
    navigate(`/leads?${params.toString()}`);
  }
  function openRoutesList(filter: "pool" | "in_progress") {
    if (filter === "pool") {
      navigate(`/routes?driver=pool`);
    } else {
      // Reuse the existing list page; status filter not yet wired there but
      // pool/in-progress can be filtered visually since both are common.
      navigate(`/routes?status=in_progress${userParam ? `&assignedTo=${userParam}` : ""}`);
    }
  }

  return (
    <div className="space-y-6 px-4 py-4 sm:px-8 sm:py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Dashboard</h2>
          <p className="mt-1 text-sm text-slate-600">
            {isAdmin
              ? "Team operations at a glance — close-rates, follow-ups, DNCs, and routes in motion."
              : "Your activity this period plus your active route."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs">
              <button
                type="button"
                onClick={() => setScope("mine")}
                className={scope === "mine" ? "bg-brand-600 px-3 py-1.5 font-semibold text-white" : "bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"}
              >
                Mine
              </button>
              <button
                type="button"
                onClick={() => setScope("team")}
                className={scope === "team" ? "bg-brand-600 px-3 py-1.5 font-semibold text-white" : "bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"}
              >
                Team
              </button>
            </div>
          )}
          <RangeTabs current={range.preset} onChange={(p) => {
            if (p === "custom") {
              setRange(buildRange("custom", { from: parseISO(customFrom), to: parseISO(customTo) }));
            } else {
              setRange(buildRange(p));
            }
          }} />
        </div>
      </div>

      {range.preset === "custom" && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-white p-3 text-xs">
          <label className="inline-flex items-center gap-2">
            From
            <input
              type="date"
              value={customFrom}
              onChange={(e) => {
                setCustomFrom(e.target.value);
                setRange(buildRange("custom", { from: parseISO(e.target.value), to: parseISO(customTo) }));
              }}
              className="rounded-md border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
          <label className="inline-flex items-center gap-2">
            To
            <input
              type="date"
              value={customTo}
              onChange={(e) => {
                setCustomTo(e.target.value);
                setRange(buildRange("custom", { from: parseISO(customFrom), to: parseISO(e.target.value) }));
              }}
              className="rounded-md border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Routes in progress"
          value={k?.routes_in_progress ?? "—"}
          Icon={RouteIcon}
          subtle="Now"
          onClick={() => openRoutesList("in_progress")}
        />
        <KpiCard
          label="Pool routes"
          value={k?.pool_routes ?? "—"}
          Icon={Users}
          subtle="Available to claim"
          onClick={() => openRoutesList("pool")}
        />
        <KpiCard
          label="Sold"
          value={k?.closed ?? "—"}
          Icon={Award}
          subtle={rangeLabel(range)}
          onClick={() => openLeadsList("closed")}
        />
        <KpiCard
          label="Follow-ups booked"
          value={k?.follow_up ?? "—"}
          Icon={CalendarCheck}
          subtle={rangeLabel(range)}
          onClick={() => openLeadsList("follow_up")}
        />
        <KpiCard
          label="No answer"
          value={k?.no_answer ?? "—"}
          // Door-closed icon, not a phone — this is a door-knocking route app.
          // "No answer" means "knocked, nobody opened", not "called, no pickup".
          Icon={DoorClosed}
          subtle={rangeLabel(range)}
          onClick={() => openLeadsList("no_answer")}
        />
        <KpiCard
          label="DNC"
          value={k?.dnc ?? "—"}
          Icon={Ban}
          subtle={rangeLabel(range)}
          onClick={() => openLeadsList("do_not_solicit")}
        />
      </section>

      {kpisQuery.isError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-800">
          KPIs failed to load: {(kpisQuery.error as Error).message}
        </div>
      )}

      <AppointmentsWidget isAdmin={isAdmin} />

      {!isAdmin && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">Your active route</h3>
          {myActiveRoute ? (
            <div className="mt-3 flex items-center justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{myActiveRoute.name}</p>
                <p className="text-xs text-slate-600">
                  {format(parseISO(myActiveRoute.route_date), "EEE, MMM d")} · {myActiveRoute.stop_count} stops
                </p>
              </div>
              <Link
                to={`/routes/${myActiveRoute.id}`}
                className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
              >
                Open route
              </Link>
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-600">No active route assigned. Check Routes to claim from the pool.</p>
          )}
        </section>
      )}

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Routes workspace</h3>
            <p className="text-xs text-slate-600">Plan, claim, and execute routes from one place.</p>
          </div>
          <Link
            to="/routes"
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Open routes
          </Link>
        </div>
      </section>
    </div>
  );
}

// ---------- Subcomponents ---------------------------------------------------

/**
 * Upcoming appointments — the driver's commitments, overdue first. RLS scopes
 * rows server-side (admins see the team, agents see their own), so the widget
 * renders for everyone. Done/Cancel act inline; realtime + the reminder hook
 * keep it fresh without reloads.
 */
function AppointmentsWidget({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const apptsQuery = useQuery({
    queryKey: ["appointments", "open"],
    queryFn: () => listOpenAppointments(),
    staleTime: 30_000,
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "completed" | "cancelled" | "missed" }) =>
      updateAppointmentStatus(id, status),
    onSuccess: (_d, vars) => {
      toast.success(vars.status === "completed" ? "Appointment marked done" : "Appointment cancelled");
      qc.invalidateQueries({ queryKey: ["appointments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const appts = apptsQuery.data ?? [];
  const now = Date.now();
  const overdue = appts.filter((a) => new Date(a.scheduled_at).getTime() < now);
  const upcoming = appts.filter((a) => new Date(a.scheduled_at).getTime() >= now);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
            <CalendarCheck className="h-4 w-4 text-brand-600" /> Appointments
          </h3>
          <p className="text-xs text-slate-600">
            {isAdmin ? "Team-wide booked appointments — overdue first." : "Your booked appointments — overdue first."}
          </p>
        </div>
        {appts.length > 0 && (
          <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
            {appts.length} open{overdue.length > 0 ? ` · ${overdue.length} overdue` : ""}
          </span>
        )}
      </div>

      {apptsQuery.isLoading ? (
        <p className="mt-3 text-sm text-slate-500">Loading appointments…</p>
      ) : apptsQuery.isError ? (
        <p className="mt-3 text-sm text-rose-700">Appointments failed to load: {(apptsQuery.error as Error).message}</p>
      ) : appts.length === 0 ? (
        <p className="mt-3 text-sm text-slate-600">
          No open appointments. Book one from a route stop with “Set appointment”.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {[...overdue, ...upcoming].slice(0, 10).map((a) => (
            <AppointmentRowItem
              key={a.id}
              appt={a}
              isOverdue={new Date(a.scheduled_at).getTime() < now}
              showAssignee={isAdmin}
              pending={statusMut.isPending}
              onDone={() => statusMut.mutate({ id: a.id, status: "completed" })}
              onCancel={() => statusMut.mutate({ id: a.id, status: "cancelled" })}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function AppointmentRowItem({
  appt,
  isOverdue,
  showAssignee,
  pending,
  onDone,
  onCancel,
}: {
  appt: AppointmentRow;
  isOverdue: boolean;
  showAssignee: boolean;
  pending: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const when = parseISO(appt.scheduled_at);
  const address = appointmentAddress(appt);
  const phoneHref = toTelHref(appt.lead?.phone);
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900">
          {appointmentLeadName(appt)}
          {showAssignee && appt.assignee?.full_name ? (
            <span className="ml-2 text-xs font-normal text-slate-500">— {appt.assignee.full_name}</span>
          ) : null}
        </p>
        <p className={`text-xs font-medium ${isOverdue ? "text-rose-600" : "text-slate-600"}`}>
          {isOverdue ? "Overdue — " : isToday(when) ? "Today — " : ""}
          {format(when, "EEE, MMM d 'at' h:mm a")}
        </p>
        {address && <p className="truncate text-xs text-slate-500">{address}</p>}
        {appt.notes && <p className="truncate text-xs italic text-slate-400">{appt.notes}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {phoneHref && (
          <a
            href={phoneHref}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
          >
            <Phone className="h-3 w-3" /> {formatPhoneDisplay(appt.lead?.phone ?? "") || "Call"}
          </a>
        )}
        <button
          type="button"
          onClick={onDone}
          disabled={pending}
          className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
        >
          Done
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </li>
  );
}

const RANGE_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom" },
];

function RangeTabs({ current, onChange }: { current: RangePreset; onChange: (p: RangePreset) => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs">
      {RANGE_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            current === o.value
              ? "bg-brand-600 px-3 py-1.5 font-semibold text-white"
              : "bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function rangeLabel(r: Range): string {
  if (r.preset === "today") return "Today";
  if (r.preset === "7d") return "Last 7 days";
  if (r.preset === "30d") return "Last 30 days";
  if (r.preset === "all") return "All time";
  if (r.from && r.to) return `${format(r.from, "MMM d")} – ${format(r.to, "MMM d")}`;
  return "Custom range";
}

/**
 * Uniform brand-toned KPI card. Earlier iterations used per-card colour
 * tints (sky/emerald/rose/amber/…) — that broke brand cohesion and gave the
 * dashboard a Christmas-tree feel. The product is a single-purpose route
 * tool; one accent (brand blue) is enough. Visual differentiation now comes
 * from the icon shape + label, not from rainbow tints.
 */
function KpiCard({
  label,
  value,
  Icon,
  subtle,
  onClick,
}: {
  label: string;
  value: number | string;
  Icon: typeof MapPin;
  subtle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-brand-300 hover:bg-brand-50/40 focus:outline-none focus:ring-2 focus:ring-brand-300"
    >
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
        <p className="mt-2 text-3xl font-semibold leading-none text-slate-900">{value}</p>
        <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">{subtle}</p>
      </div>
      <Icon className="h-5 w-5 shrink-0 text-brand-600 transition group-hover:text-brand-700" />
    </button>
  );
}
